import { useCallback, useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { getCurrentUser } from '../auth/useUser';
import { resolveActorName } from '../auth/actorNames';
import { useAppData } from '../state/contexts/AppDataContext';
import {
  approveRevetChange,
  completeRevetChange,
  declineRevetChange,
  getRevetChange,
  withdrawRevetChange,
  type OpenRevetChange,
  type RevetKind,
  type RevetLogoSide,
} from '../api/revetChange';
import { COMPANY_FIELD_LABEL, formatCompanyFieldValue } from '../api/intake';
import { logoSnapshotDataUrl } from '../api/logoSnapshots';

// #478 — the change-driven re-vet, on the case it is about.
//
// ONE PANEL, ON THE ONBOARDING TAB, and not split between the tabs its two acts
// might each belong to. Completion is verification work and approval is a
// sign-off, so an argument exists for putting them on Onboarding and Decision
// respectively — but they are two acts on ONE thing, and separating them would
// mean the approver reads the requested value on one tab and signs for it on
// another. It sits here because this is where the fields it changes are
// rendered: the legal name and the address the analyst is comparing against
// evidence are three inches below.
//
// THE REQUESTED VALUE IS NOT ON THE CASE, and the panel fetches it separately
// for that reason. Until an approver signs off, `cases.registrant` still holds
// the OLD name — deliberately, because it must only ever hold a name that has
// passed dual control. A panel that read the new value off the case would have
// nothing to read.
//
// The same-person warning here is UX. The enforcement is the
// `revet_dual_control_distinct_principals` CHECK in the database, which is why
// the Approve button is still rendered (disabled) rather than hidden: somebody
// who cannot approve their own work should be told why, not left looking for a
// button that is not there.
//
// #768 — THE SAME PANEL FOR THE LOGO RE-VET, mounted a second time on the
// RCD/SHAFT tab with `kind="logo"`, because that is where the logo and its
// SHAFT review live. Each mount shows only its own kind, so the Onboarding tab
// (which mounts the default) is unchanged. The logo kind shows the delivered
// logo and the candidate SIDE BY SIDE, both as the server's stored copies
// (api/logoSnapshots.ts — `data:` URLs read with the bearer token; never the
// registrant's URL, which the CSP refuses and which would tell them who looked).

/** The two images a logo re-vet compares, as `data:` URLs; null until loaded or when there is none. */
function useLogoPair(caseId: string, candidate: RevetLogoSide | null | undefined, delivered: RevetLogoSide | null | undefined) {
  const [images, setImages] = useState<{ candidate: string | null; delivered: string | null }>({ candidate: null, delivered: null });
  const candidateId = candidate?.outcome === 'ok' ? candidate.snapshotId : null;
  const deliveredId = delivered?.outcome === 'ok' ? delivered.snapshotId : null;
  useEffect(() => {
    let live = true;
    const read = (id: number | null) => (id === null ? Promise.resolve(null) : logoSnapshotDataUrl(caseId, id).catch(() => null));
    void Promise.all([read(candidateId), read(deliveredId)]).then(([c, d]) => {
      if (live) setImages({ candidate: c, delivered: d });
    });
    return () => { live = false; };
  }, [caseId, candidateId, deliveredId]);
  return images;
}

function LogoSideView({ title, side, image }: { title: string; side: RevetLogoSide | null | undefined; image: string | null }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>{title}</div>
      {!side && <div style={{ fontSize: 12, color: C.textMuted }}>None reviewed yet — nothing is delivered for it</div>}
      {side && side.outcome === 'pending' && <div style={{ fontSize: 12, color: C.textMuted }}>Still being fetched…</div>}
      {side && side.outcome === 'failed' && (
        <div style={{ fontSize: 12, color: C.danger }}>Could not be fetched: {side.failureReason ?? 'no reason recorded'}</div>
      )}
      {side && side.outcome === 'ok' && (image
        ? <img src={image} alt={title} style={{ maxWidth: 128, maxHeight: 128, border: `1px solid ${C.border}` }} />
        : <div style={{ fontSize: 12, color: C.textMuted }}>Loading image…</div>)}
      {side && (
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, wordBreak: 'break-all' }}>
          <span style={{ userSelect: 'all' }}>{side.sourceUrl}</span>
          {side.sha256 && <div>SHA-256 {side.sha256.slice(0, 12)}…</div>}
        </div>
      )}
    </div>
  );
}

export function RevetChangePanel({ caseId, kind = 'profile' }: { caseId: string; kind?: RevetKind }) {
  const { showToast, mergeLocalCase } = useAppData();
  const user = getCurrentUser();
  const [state, setState] = useState<OpenRevetChange | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await getRevetChange(caseId));
    } catch {
      // A panel that cannot load its own state says nothing rather than
      // claiming there is no re-vet — "no banner" and "the fetch failed" must
      // not look the same on a screen where the banner is the whole warning.
      setState(null);
    } finally {
      setLoaded(true);
    }
  }, [caseId]);

  useEffect(() => { void load(); }, [load]);
  const images = useLogoPair(caseId, state?.kind === 'logo' ? state.candidate : null, state?.kind === 'logo' ? state.delivered : null);

  if (!loaded || !state || !user) return null;
  // Each mount shows its own kind only (see the header).
  if ((state.kind ?? 'profile') !== kind) return null;
  if (kind === 'profile' && !state.changeRequestId) return null;
  const isLogo = kind === 'logo';
  // Nobody can review or sign off an image that is not there (the API refuses
  // with 409 too); said here so the buttons are not a dead end.
  const candidateReady = !isLogo || state.candidate?.outcome === 'ok';

  const completed = state.completedBy !== null;
  // The server ID space (CLAUDE.md): completedBy is a `users.id`, so it is
  // compared against serverId. Comparing it against `user.id` — the Keycloak
  // sub — is always a bug, and here it would be a silent one that let the
  // warning never fire.
  const completedByMe = completed && state.completedBy === user.serverId;
  const canComplete = user.canEdit && !completed && candidateReady;
  const canSignOff = user.canApprove && completed && candidateReady;
  // #814 — the way out of a logo re-vet that cannot finish. Not once an
  // analyst has completed it: that one is waiting for a sign-off, which
  // approves or declines it (the API refuses with 409 too).
  const canWithdraw = isLogo && user.canApprove && !completed;

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const detail = (err as { detail?: string; message?: string }).detail
        ?? (err as { message?: string }).message ?? 'the action failed';
      showToast(detail, 'error');
    } finally {
      setBusy(false);
    }
  };

  const requestedRow = (field: string) => {
    const before = field === 'legalName'
      ? state.current.legalName ?? ''
      : formatCompanyFieldValue(field, state.current.businessAddress);
    const after = formatCompanyFieldValue(field, state.requested[field]);
    return (
      <div key={field} style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>
          {COMPANY_FIELD_LABEL[field] ?? field}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 10, color: C.textMuted }}>On this registration now</div>
            <div>{before || <span style={{ color: C.textMuted }}>—</span>}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.textMuted }}>Requested</div>
            <div style={{ fontWeight: 600 }}>{after || <span style={{ color: C.textMuted }}>—</span>}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      ...styles.card,
      border: `2px solid ${C.warn}`,
      background: C.warnLight,
      marginBottom: 16,
    }}>
      {isLogo ? (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: C.warn, marginBottom: 8 }}>
            Logo re-vet open{state.changeRequestId ? ` — company change request ${state.changeRequestId}` : ''}
          </h4>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
            The call logo has changed, and CTIA is still sent the delivered logo on the left. An analyst
            re-runs the SHAFT review on the new one, and a different approver signs off — and that
            sign-off is what makes the new logo the one sent to the Settlement Platform.
          </div>
          <div
            role="group"
            aria-label="Delivered and new logo"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}
          >
            <LogoSideView title="Delivered to CTIA now" side={state.delivered} image={images.delivered} />
            <LogoSideView title="New logo under review" side={state.candidate} image={images.candidate} />
          </div>
        </>
      ) : (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: C.warn, marginBottom: 8 }}>
            Re-vet open — company change request {state.changeRequestId}
          </h4>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
            This registration has NOT been changed. The customer asked for the values below; an analyst
            verifies them against evidence, and a different approver signs off — and that sign-off is
            what writes them onto this case and re-asserts the identity to the Settlement Platform.
          </div>

          {state.fields.map(requestedRow)}
        </>
      )}

      {completed && (
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
          Verified by {resolveActorName({ actorId: state.completedBy, actorName: state.completedByName })} on {fmtDateTime(state.completedAt)}.
          Awaiting a second signature.
        </div>
      )}

      {canComplete && (
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="revet-evidence" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>
            {isLogo
              ? 'Your SHAFT re-review of the new logo (required — the approver has only this to go on)'
              : 'What you verified, and against what (required — the approver has only this to go on)'}
          </label>
          <textarea
            id="revet-evidence"
            style={{ ...styles.textarea, fontSize: 12, minHeight: 60 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button
            style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall, marginTop: 8 }}
            disabled={busy || note.trim().length === 0}
            onClick={() => act(async () => {
              await completeRevetChange(caseId, note.trim());
              setNote('');
              await load();
              showToast('Re-vet completed — a different approver has to sign it off', 'success');
            })}
          >
            Complete re-vet
          </button>
        </div>
      )}

      {!completed && !canComplete && (
        <div style={{ fontSize: 12, color: C.textMuted }}>
          {candidateReady
            ? 'An analyst has to verify this before it can be signed off.'
            : 'The new logo has no image yet, so it cannot be reviewed. Use Re-fetch logo below — while this re-vet is open it fetches the URL the customer asked for.'}
        </div>
      )}

      {canSignOff && completedByMe && (
        <div style={{ fontSize: 12, color: C.warn, fontWeight: 600, marginBottom: 8 }}>
          ⚠ You completed this re-vet, so you cannot also approve it. A second person has to sign it
          off — holding both roles is not being two people, and the database refuses it either way.
        </div>
      )}

      {canSignOff && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <button
            style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }}
            disabled={busy || completedByMe}
            onClick={() => act(async () => {
              const result = await approveRevetChange(caseId);
              // Fold what the server actually wrote into the local store, so
              // the case header stops showing the old registrant without a
              // reload. Only what it reported writing — an optimistic patch
              // that guessed would be the lie this whole feature exists to
              // avoid.
              const patch: Record<string, unknown> = { revetStatus: 'COMPLETED' };
              if (result.written.includes('legalName')) {
                patch.registrant = String(state.requested.legalName ?? '');
              }
              if (result.syncQueued) patch.settlementSyncStatus = 'PENDING_SYNC';
              mergeLocalCase(caseId, patch as never);
              await load();
              showToast(
                result.warning
                  ?? (isLogo
                    ? (result.syncQueued
                      ? 'Approved — the new logo is the one CTIA is sent; a Settlement sync is queued'
                      : 'Approved — the new logo is recorded. No sync was queued: it matches what CTIA has, or the case holds no approval to deliver on.')
                    : (result.syncQueued
                      ? 'Approved — the registration is updated and a Settlement sync is queued'
                      : 'Approved — the registration is updated. Nothing is sent to CTIA: the platform carries no address.')),
                result.warning ? 'error' : 'success',
              );
            })}
          >
            {isLogo ? 'Approve the new logo' : 'Approve and write to the case'}
          </button>
          <button
            style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }}
            disabled={busy}
            onClick={() => setDeclining(true)}
          >
            Decline
          </button>
        </div>
      )}

      {declining && (
        <div style={{ marginTop: 12 }}>
          <label htmlFor="revet-decline" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>
            Why (required — the analyst has to know what to re-verify)
          </label>
          <textarea
            id="revet-decline"
            style={{ ...styles.textarea, fontSize: 12, minHeight: 60 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
            {isLogo
              ? 'CTIA keeps the delivered logo. The re-vet goes back to the analyst, and stays open until a logo is approved or the re-vet is withdrawn — a re-fetch that finds a different image replaces the one under review.'
              : 'Nothing is rolled back — nothing was written. The re-vet goes back to the analyst, and this registration keeps blocking a second change request until it is resolved.'}
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall, marginTop: 8 }}
            disabled={busy || reason.trim().length === 0}
            onClick={() => act(async () => {
              await declineRevetChange(caseId, reason.trim());
              setReason('');
              setDeclining(false);
              await load();
              showToast('Declined — back with the analyst', 'success');
            })}
          >
            Confirm decline
          </button>
        </div>
      )}

      {canWithdraw && !withdrawing && (
        <div style={{ marginTop: 12 }}>
          <button
            style={{ ...styles.btn, ...styles.btnSmall }}
            disabled={busy}
            onClick={() => setWithdrawing(true)}
          >
            Withdraw this re-vet
          </button>
        </div>
      )}

      {withdrawing && (
        <div style={{ marginTop: 12 }}>
          <label htmlFor="revet-withdraw" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>
            {state.changeRequestId
              ? 'Why (required — it is recorded, and the customer is shown it)'
              : 'Why (required — it is recorded)'}
          </label>
          <textarea
            id="revet-withdraw"
            style={{ ...styles.textarea, fontSize: 12, minHeight: 60 }}
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.target.value)}
          />
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
            {state.changeRequestId
              ? `CTIA keeps the delivered logo. Company change request ${state.changeRequestId} is marked not `
                + 'accepted with this reason, and the customer is sent an email about it — unless they have '
                + 'switched those off, or the portal\'s Keycloak admin connection is not configured; they can '
                + 'send a new request.'
              : 'CTIA keeps the delivered logo. The re-vet closes, and the registration takes company changes '
                + 'and logo URL edits again. The withdrawal does not remember this image: any later fetch or '
                + 're-decision that finds it as the newest image at the logo address can open a new re-vet on '
                + 'it, which can be withdrawn again.'}
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall, marginTop: 8 }}
            disabled={busy || withdrawReason.trim().length === 0}
            onClick={() => act(async () => {
              const result = await withdrawRevetChange(caseId, withdrawReason.trim());
              setWithdrawReason('');
              setWithdrawing(false);
              // The annual re-vet's status, as the server left it (SCHEDULED
              // when one is due, else null).
              mergeLocalCase(caseId, { revetStatus: result.revetStatus } as never);
              await load();
              showToast(
                result.changeRequestRejected
                  ? 'Withdrawn — the customer\'s request is marked not accepted, with your reason'
                  : 'Withdrawn — CTIA keeps the delivered logo',
                'success',
              );
            })}
          >
            Confirm withdrawal
          </button>
        </div>
      )}
    </div>
  );
}
